import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { color, typographySize } from '@shared/constants/Styles';
import { type JSX, useState } from 'react';
import { ClickableCard } from './LegacyCard';

interface IMenuCardProps {
    title: string;
    startExpanded?: boolean;
    leftIcons?: { src: string; alt: string; tooltip?: string }[];
    id?: string;
    rightComponent?: () => JSX.Element;
    children: React.ReactNode;
}

const ExpanderCard: React.FC<IMenuCardProps> = ({ title, startExpanded, children, leftIcons, id, rightComponent }) => {
    const [isExpanded, setIsExpanded] = useState(startExpanded);
    return (
        <MarginWrapper>
            <WrapperButton id={id} onClick={() => setIsExpanded(!isExpanded)}>
                <ActionCardWrapper data-expanded={isExpanded}>
                    {leftIcons && leftIcons.length > 0 && (
                        <IconContainer>
                            {leftIcons?.map((icon, index) => (
                                <Tooltip key={index} title={icon.tooltip}>
                                    <CardIcon src={icon.src} alt={icon.alt} />
                                </Tooltip>
                            ))}
                        </IconContainer>
                    )}
                    <ExpanderTitle>{title}</ExpanderTitle>
                    {rightComponent && rightComponent()}
                </ActionCardWrapper>
            </WrapperButton>
            {isExpanded && <ExpandedSection>{children}</ExpandedSection>}
        </MarginWrapper>
    );
};
const MarginWrapper = styled.div`
    margin-bottom: 16px;
`;

const IconContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-right: auto;
    margin-left: 16px;
    position: absolute;
    left: 16px;
`;

const CardIcon = styled.img`
    width: 32px;
    height: 32px;
    margin-right: 8px;
`;

const WrapperButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    width: 100%;
    position: relative;
`;

const ExpandedSection = styled.div`
    background-color: ${(props) => props.theme.color.accentCard};
    animation: float 0.25s ease-in-out;
    margin: 0 16px 32px 16px;
    padding: 1rem 1rem 2rem 1rem;
    border-radius: 0 0 10px 10px;
    @keyframes float {
        0% {
            opacity: 0;
            transform: translatey(0px);
        }
        100% {
            transform: translatey(0px);
            opacity: 1;
        }
    }
    @keyframes fade {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
`;
export const ExpanderTitle = styled.h3`
    font-size: ${typographySize.card_title};
    font-weight: lighter;
    color: ${(props) => props.theme.color.text};
    margin: 0 88px;
    flex: 1;
`;

export const ActionCardWrapper = styled(ClickableCard)`
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-self: center;
    cursor: pointer;
    width: calc(100% - 32px);
    min-width: 380px;
    padding: 16px;
    margin: 0 16px;
    background-color: ${(props) => props.theme.color.accentCard};
    color: ${color.secondary};
    &[data-expanded='true'] {
        box-shadow: none;
        border: 1px solid ${color.primary_dark}40;
        border-radius: 10px 10px 0 0;
        border-bottom: none;
    }
`;

export default ExpanderCard;
